import React, { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Nowy panel do tworzenia i zarządzania grupami "sezonowymi":
 *  - zapis do kolekcji: sessions_groups
 *  - generowanie zajęć do kolekcji: sessions
 *  - wybór bazowej grupy z mapy "groups/SPdqELFz2ovWSn8eFyws"
 *  - pełna edycja (pól + lista userów)
 *  - operacje "od daty" na sessions (dodaj/usuń uczestnika od wskazanej daty)
 */

const TARGET_FACILITY = 'SPdqELFz2ovWSn8eFyws';

const normalizeName = (s = '') => s.toString().trim().replace(/\s+/g, ' ');
const normalizePhoneDigits = (s = '') => s.toString().replace(/\D+/g, '');
const toISODate = (d) => new Date(d).toISOString().split('T')[0];

export default function GroupsAdmin() {
  /** Tryby panelu */
  const [mode, setMode] = useState('list'); // 'list' | 'create' | 'manage'

  /** Dane do tworzenia nowej sessions_group */
  const [baseGroupsMap, setBaseGroupsMap] = useState({}); // z groups/TARGET_FACILITY (mapa)
  const [baseGroupId, setBaseGroupId] = useState('');
  const [locations, setLocations] = useState([]);
  const [facilities, setFacilities] = useState([]);

  const [form, setForm] = useState({
    facility_id: TARGET_FACILITY,
    location_id: '',
    groupId: '', // id z mapy bazowej
    type: '',
    color: '',
    minAge: 0,
    maxAge: 99,
    exp: '',
    adults: false,
    prices: { contract: 0, entry: 0, obligation: '' },
    slots: 1,
    startDate: '', // YYYY-MM-DD
    endDate: '',   // YYYY-MM-DD
    days: [{ dayOfWeek: '', startTime: '', endTime: '', instructor: '', name: '' }],
    users: [], // {uid, displayName, phone, gender, info}
  });

  /** Zarządzanie istniejącymi sessions_groups */
  const [sgList, setSgList] = useState([]); // lista sessions_groups (dla TARGET_FACILITY)
  const [selectedSG, setSelectedSG] = useState(null); // pełny dokument wybranej sessions_groups
  const [editSG, setEditSG] = useState(null); // kopia do formularza edycyjnego

  /** Error/info */
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  /** --- Ładowanie mapy bazowych grup (groups/TARGET_FACILITY) --- */
  useEffect(() => {
    const ref = doc(db, 'groups', TARGET_FACILITY);
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.data() || {};
      setBaseGroupsMap(data); // to jest mapa: { [groupId]: { ... } }
    });
    return () => unsub();
  }, []);

  /** --- Lokalizacje (mapa -> tablica) --- */
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'locations'), (snapshot) => {
      const buckets = snapshot.docs.map((d) => d.data());
      const list = [];
      buckets.forEach((b) => Object.values(b || {}).forEach((l) => l?.id && list.push(l)));
      setLocations(list);
    });
    return () => unsub();
  }, []);

  /** --- Placówki (jeśli chcesz wybierać; domyślnie TARGET_FACILITY) --- */
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'facilities'), (snapshot) => {
      setFacilities(snapshot.docs.map((d) => d.data()));
    });
    return () => unsub();
  }, []);

  /** --- Lista istniejących sessions_groups dla panelu "manage" --- */
  useEffect(() => {
    if (mode !== 'manage' && mode !== 'list') return;
    const qSg = query(collection(db, 'sessions_groups'), where('facility_id', '==', TARGET_FACILITY));
    const unsub = onSnapshot(qSg, (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSgList(arr.sort((a, b) => (a?.location_id || '').localeCompare(b?.location_id || '')));
    });
    return () => unsub();
  }, [mode]);

  /** --- Prefill po wyborze bazowej grupy --- */
  useEffect(() => {
    if (!baseGroupId) return;
    const g = baseGroupsMap[baseGroupId];
    if (!g) return;

    // Wyciągnij days (może być tablica/pojedynczy)
    const daysArr = Array.isArray(g?.days) ? g.days : g?.days ? [g.days] : [];
    const prefillDays = daysArr.map((d) => ({
      dayOfWeek: d?.weekDay ?? '',
      startTime: d?.startTime ?? '',
      endTime: d?.endTime ?? '',
      instructor: g?.instructor || '',
      name: d?.custom_name || g?.type || '',
    }));

    setForm((prev) => ({
      ...prev,
      groupId: baseGroupId,
      location_id: g?.location_id || prev.location_id,
      type: g?.type || prev.type,
      color: g?.color || prev.color,
      minAge: g?.minAge ?? prev.minAge,
      maxAge: g?.maxAge ?? prev.maxAge,
      exp: g?.exp || prev.exp,
      adults: !!g?.adults,
      prices: {
        contract: g?.prices?.contract ?? prev?.prices?.contract ?? 0,
        entry: g?.prices?.entry ?? prev?.prices?.entry ?? 0,
        obligation: g?.prices?.obligation ?? prev?.prices?.obligation ?? '',
      },
      days: prefillDays.length ? prefillDays : prev.days,
    }));
  }, [baseGroupId, baseGroupsMap]);

  /** --- Wczytanie użytkowników z users2026 dla wybranej bazowej grupy --- */
  const loadUsersFromUsers2026 = async (groupId) => {
    // Przejdź po users2026 i zbierz subUsers, które mają groups zawierające groupId
    const snap = await getDocs(collection(db, 'users2026'));
    const result = [];
    snap.forEach((docu) => {
      const acc = docu.data();
      const subs = acc?.subUsers || {};
      Object.values(subs).forEach((su) => {
        const inGroup = Array.isArray(su?.groups) && su.groups.includes(groupId);
        if (!inGroup) return;

        // pickUp info
        const pick = (su?.pickUp || '').toString().toLowerCase();
        const cls = su?.class;
        let info = '';
        if (pick && pick !== 'nie') {
          info = 'Odebrać' + (cls ? ` i ${cls}` : '');
        }

        result.push({
          uid: acc?.uid || '',
          displayName: normalizeName(su?.displayName || ''),
          phone: acc?.parent?.phone || '',
          gender: su?.gender || '',
          info,
        });
      });
    });

    // de-dupe po (uid + displayName + phone)
    const uniq = new Map();
    result.forEach((u) => {
      const key = `${u.uid}|${normalizeName(u.displayName)}|${normalizePhoneDigits(u.phone)}`;
      if (!uniq.has(key)) uniq.set(key, u);
    });
    return Array.from(uniq.values());
  };

  /** --- Fallback: wczytanie z book-list/{groupId} --- */
  const loadUsersFromBookList = async (groupId) => {
    const dref = doc(db, 'book-list', groupId);
    const ds = await getDoc(dref);
    const arr = ds.exists() ? (ds.data()?.users || []) : [];
    return arr.map((u) => ({
      uid: u?.uid || '',
      displayName: normalizeName(u?.displayName || ''),
      phone: u?.phone || '',
      gender: u?.gender || '',
      info: u?.info || '',
    }));
  };

  const prefillUsers = async () => {
    try {
      setStatus('Wczytuję użytkowników…');
      const gid = baseGroupId || form.groupId;
      if (!gid) {
        setStatus('');
        setError('Wybierz najpierw bazową grupę.');
        return;
      }
      let users = await loadUsersFromUsers2026(gid);
      if (users.length === 0) {
        users = await loadUsersFromBookList(gid);
      }
      setForm((prev) => ({ ...prev, users }));
      setError('');
      setStatus(`Wczytano ${users.length} użytkowników.`);
    } catch (e) {
      console.error(e);
      setError('Błąd wczytywania użytkowników.');
      setStatus('');
    }
  };

  /** --- Generowanie dat i tworzenie sessions --- */
  const generateSessionDates = (startDate, endDate, days) => {
    const dates = [];
    let cursor = new Date(startDate);
    const end = new Date(endDate);

    const dlist = Array.isArray(days) ? days : [];
    while (cursor <= end) {
      dlist.forEach((d) => {
        const dow = parseInt(d?.dayOfWeek, 10);
        if (Number.isInteger(dow) && cursor.getDay() === dow) {
          dates.push({
            date: new Date(cursor),
            startTime: d?.startTime || '',
            endTime: d?.endTime || '',
            instructor: d?.instructor || '',
            name: d?.name || '',
          });
        }
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    return dates;
  };

  const createSessionsForGroup = async (sgId, payload) => {
    const { facility_id, location_id, days, startDate, endDate, users, slots, color, minAge, maxAge, exp } = payload;
    const sessionDates = generateSessionDates(startDate, endDate, days);

    for (const it of sessionDates) {
      const sref = doc(collection(db, 'sessions'));
      await setDoc(
        sref,
        {
          id: sref.id,
          group_id: sgId, // <- powiązanie z sessions_groups
          facility_id,
          location_id,
          date: toISODate(it.date),
          startTime: it.startTime,
          endTime: it.endTime,
          instructors: [it.instructor].filter(Boolean),
          users: (users || []).map((u) => ({
            ...u,
            toBe: false,
          })),
          status: 'scheduled',
          slots: slots || 1,
          minAge,
          maxAge,
          exp,
          color: color || '',
          type: 'lesson',
          name: it.name || '',
        },
        { merge: true }
      );
    }
  };

  /** --- Tworzenie nowej sessions_group + sessions --- */
  const createSessionsGroup = async () => {
    try {
      setError('');
      setStatus('Zapisuję…');

      // minimalne walidacje
      if (!form.facility_id || !form.location_id || !form.startDate || !form.endDate || !form.days?.length) {
        setStatus('');
        setError('Uzupełnij wymagane pola: placówka, lokalizacja, zakres dat, dni.');
        return;
      }

      const payload = {
        facility_id: form.facility_id,
        location_id: form.location_id,
        groupId: form.groupId || baseGroupId || '',
        type: form.type || '',
        color: form.color || '',
        minAge: Number(form.minAge || 0),
        maxAge: Number(form.maxAge || 99),
        exp: form.exp || '',
        adults: !!form.adults,
        prices: {
          contract: Number(form?.prices?.contract || 0),
          entry: Number(form?.prices?.entry || 0),
          obligation: form?.prices?.obligation || '',
        },
        slots: Number(form.slots || 1),
        startDate: form.startDate,
        endDate: form.endDate,
        days: (form.days || []).map((d) => ({
          dayOfWeek: parseInt(d?.dayOfWeek || '', 10),
          startTime: d?.startTime || '',
          endTime: d?.endTime || '',
          instructor: d?.instructor || '',
          name: d?.name || '',
        })),
        users: Array.isArray(form.users) ? form.users : [],
      };

      // 1) sessions_groups
      const sgRef = await addDoc(collection(db, 'sessions_groups'), payload);

      // 2) sessions (na podstawie zakresu dat + days)
      await createSessionsForGroup(sgRef.id, payload);

      setStatus('Dodano grupę i wygenerowano zajęcia.');
      setError('');
      // reset częściowy
      setForm((prev) => ({ ...prev, users: [] }));
      setMode('manage');
    } catch (e) {
      console.error(e);
      setStatus('');
      setError('Błąd podczas tworzenia grupy.');
    }
  };

  /** ---------------------- PANEL ZARZĄDZANIA ISTNIEJĄCYMI ---------------------- */

  const openSG = (sg) => {
    setSelectedSG(sg);
    setEditSG(JSON.parse(JSON.stringify(sg))); // deep-ish copy
    setMode('manage');
  };

  const saveSGMeta = async () => {
    try {
      if (!selectedSG?.id) return;
      const up = { ...editSG };
      delete up.id;
      await updateDoc(doc(db, 'sessions_groups', selectedSG.id), up);
      setStatus('Zapisano zmiany.');
      setError('');
    } catch (e) {
      console.error(e);
      setError('Błąd zapisu zmian.');
      setStatus('');
    }
  };

  /** Dodaj/Usuń userów w sessions_groups (tylko meta) */
  const addUserToSG = (u) => {
    setEditSG((prev) => ({ ...prev, users: [...(prev?.users || []), u] }));
  };
  const removeUserFromSG = (idx) => {
    setEditSG((prev) => {
      const arr = [...(prev?.users || [])];
      arr.splice(idx, 1);
      return { ...prev, users: arr };
    });
  };

  /** Operacje OD DATY na kolekcji sessions powiązanych z tą grupą */
  const applyUsersChangeFromDate = async ({ type, user, fromDate }) => {
    // type: 'add' | 'remove'
    if (!selectedSG?.id) return;
    try {
      setStatus('Aktualizuję sesje…');

      // Pobierz sessions dla tej grupy od daty (włącznie)
      const qS = query(
        collection(db, 'sessions'),
        where('group_id', '==', selectedSG.id),
      );
      const snap = await getDocs(qS);
      const target = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((s) => (s?.date || '') >= fromDate);

      const tasks = target.map((s) => {
        const ref = doc(db, 'sessions', s.id);
        const current = Array.isArray(s?.users) ? s.users : [];
        if (type === 'remove') {
          const next = current.filter((x) => !(x?.uid === user?.uid && normalizePhoneDigits(x?.phone) === normalizePhoneDigits(user?.phone)));
          if (next.length === current.length) return Promise.resolve();
          return updateDoc(ref, { users: next });
        } else {
          // add
          // unikaj duplikatu
          const exists = current.some((x) => x?.uid === user?.uid || (normalizePhoneDigits(x?.phone) === normalizePhoneDigits(user?.phone) && normalizeName(x?.displayName) === normalizeName(user?.displayName)));
          const next = exists ? current : [...current, { ...user, toBe: false }];
          return updateDoc(ref, { users: next });
        }
      });

      await Promise.all(tasks);
      setStatus('Zaktualizowano zajęcia od wskazanej daty.');
      setError('');
    } catch (e) {
      console.error(e);
      setError('Błąd aktualizacji zajęć.');
      setStatus('');
    }
  };

  /** --- UI helpers --- */
  const baseGroupsOptions = useMemo(() => {
    return Object.entries(baseGroupsMap).map(([id, g]) => ({
      id,
      label: `${id} • ${(g?.location_id || '')} • ${(g?.days?.[0]?.custom_name || g?.type || '')}`,
    }));
  }, [baseGroupsMap]);

  const locationName = (id) => locations.find((l) => l.id === id)?.name || id || '—';

  /** --- RENDER --- */
  return (
    <div style={{ display: 'flex', gap: 24, flexDirection: 'column', padding: 24 }}>
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={() => { setMode('list'); setSelectedSG(null); setEditSG(null); }}>Lista grup</button>
        <button onClick={() => { setMode('create'); setSelectedSG(null); setEditSG(null); }}>Dodaj grupę</button>
        <button onClick={() => { setMode('manage'); setSelectedSG(null); }}>Zarządzaj</button>
      </div>

      {status && <div style={{ color: '#0a66ff', fontSize: 12 }}>{status}</div>}
      {error && <div style={{ color: '#bd203f', fontWeight: 600 }}>{error}</div>}

      {/* LISTA (skrót) */}
      {mode === 'list' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <h3>Istniejące sessions_groups – {sgList.length}</h3>
          {sgList.map((g) => (
            <div
              key={g.id}
              style={{ border: '1px solid #ddd', borderRadius: 8, padding: 10, cursor: 'pointer' }}
              onClick={() => openSG(g)}
            >
              <div style={{ fontWeight: 700 }}>{locationName(g.location_id)} — {g?.type || g?.groupId}</div>
              <div style={{ fontSize: 12, opacity: 0.8 }}>
                {g.startDate} → {g.endDate} • {g?.days?.length || 0} dni • Uczestnicy: {g?.users?.length || 0}
              </div>
            </div>
          ))}
          {!sgList.length && <div>Brak grup.</div>}
        </div>
      )}

      {/* CREATE */}
      {mode === 'create' && (
        <div style={{ display: 'grid', gap: 14 }}>
          <h3>Nowa grupa (sessions_groups)</h3>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ width: 160 }}>Bazowa grupa (groups):</span>
            <select value={baseGroupId} onChange={(e) => setBaseGroupId(e.target.value)}>
              <option value="">— wybierz —</option>
              {baseGroupsOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
            <button onClick={prefillUsers}>Wczytaj uczestników</button>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ width: 160 }}>Placówka:</span>
            <input value={form.facility_id} onChange={(e) => setForm({ ...form, facility_id: e.target.value })} />
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ width: 160 }}>Lokalizacja:</span>
            <select
              value={form.location_id}
              onChange={(e) => setForm({ ...form, location_id: e.target.value })}
            >
              <option value="">—</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ width: 160 }}>Typ:</span>
            <input value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} />
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ width: 160 }}>Kolor:</span>
            <input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <div>
              <div>Min wiek</div>
              <input
                type="number"
                value={form.minAge}
                onChange={(e) => setForm({ ...form, minAge: e.target.value })}
              />
            </div>
            <div>
              <div>Max wiek</div>
              <input
                type="number"
                value={form.maxAge}
                onChange={(e) => setForm({ ...form, maxAge: e.target.value })}
              />
            </div>
            <div>
              <div>Poziom</div>
              <input
                value={form.exp}
                onChange={(e) => setForm({ ...form, exp: e.target.value })}
              />
            </div>
            <div>
              <div>Dorośli?</div>
              <input
                type="checkbox"
                checked={!!form.adults}
                onChange={(e) => setForm({ ...form, adults: e.target.checked })}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <div>
              <div>Cena umowy</div>
              <input
                type="number"
                value={form.prices.contract}
                onChange={(e) => setForm({ ...form, prices: { ...form.prices, contract: e.target.value } })}
              />
            </div>
            <div>
              <div>Cena wejścia</div>
              <input
                type="number"
                value={form.prices.entry}
                onChange={(e) => setForm({ ...form, prices: { ...form.prices, entry: e.target.value } })}
              />
            </div>
            <div>
              <div>Umowa (obligation)</div>
              <input
                value={form.prices.obligation}
                onChange={(e) => setForm({ ...form, prices: { ...form.prices, obligation: e.target.value } })}
              />
            </div>
            <div>
              <div>Miejsca</div>
              <input
                type="number"
                value={form.slots}
                onChange={(e) => setForm({ ...form, slots: e.target.value })}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 16 }}>
            <div>
              <div>Start</div>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              />
            </div>
            <div>
              <div>Koniec</div>
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              />
            </div>
          </div>

          <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 10 }}>
            <div style={{ marginBottom: 8, fontWeight: 700 }}>Dni zajęć</div>
            {(form.days || []).map((d, idx) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '110px 120px 120px 220px 1fr', gap: 8, marginBottom: 6 }}>
                <input
                  type="number"
                  placeholder="Dzień (0-6)"
                  value={d.dayOfWeek}
                  onChange={(e) => {
                    const v = e.target.value;
                    const arr = [...form.days];
                    arr[idx] = { ...arr[idx], dayOfWeek: v };
                    setForm({ ...form, days: arr });
                  }}
                />
                <input
                  type="time"
                  placeholder="start"
                  value={d.startTime}
                  onChange={(e) => {
                    const arr = [...form.days];
                    arr[idx] = { ...arr[idx], startTime: e.target.value };
                    setForm({ ...form, days: arr });
                  }}
                />
                <input
                  type="time"
                  placeholder="koniec"
                  value={d.endTime}
                  onChange={(e) => {
                    const arr = [...form.days];
                    arr[idx] = { ...arr[idx], endTime: e.target.value };
                    setForm({ ...form, days: arr });
                  }}
                />
                <input
                  placeholder="instruktor (email)"
                  value={d.instructor}
                  onChange={(e) => {
                    const arr = [...form.days];
                    arr[idx] = { ...arr[idx], instructor: e.target.value };
                    setForm({ ...form, days: arr });
                  }}
                />
                <input
                  placeholder="Nazwa"
                  value={d.name}
                  onChange={(e) => {
                    const arr = [...form.days];
                    arr[idx] = { ...arr[idx], name: e.target.value };
                    setForm({ ...form, days: arr });
                  }}
                />
              </div>
            ))}
            <button
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  days: [...(prev.days || []), { dayOfWeek: '', startTime: '', endTime: '', instructor: '', name: '' }],
                }))
              }
            >
              + Dodaj dzień
            </button>
          </div>

          <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 10 }}>
            <div style={{ marginBottom: 8, fontWeight: 700 }}>Uczestnicy</div>
            {(form.users || []).map((u, idx) => (
              <div key={`${u.uid || 'x'}-${idx}`} style={{ display: 'grid', gridTemplateColumns: '1fr 140px 100px 1fr 80px', gap: 6, marginBottom: 6 }}>
                <input
                  value={u.displayName}
                  onChange={(e) => {
                    const arr = [...form.users];
                    arr[idx] = { ...arr[idx], displayName: e.target.value };
                    setForm({ ...form, users: arr });
                  }}
                />
                <input
                  value={u.phone}
                  onChange={(e) => {
                    const arr = [...form.users];
                    arr[idx] = { ...arr[idx], phone: e.target.value };
                    setForm({ ...form, users: arr });
                  }}
                  placeholder="Telefon"
                />
                <input
                  value={u.gender}
                  onChange={(e) => {
                    const arr = [...form.users];
                    arr[idx] = { ...arr[idx], gender: e.target.value };
                    setForm({ ...form, users: arr });
                  }}
                  placeholder="Płeć"
                />
                <input
                  value={u.info || ''}
                  onChange={(e) => {
                    const arr = [...form.users];
                    arr[idx] = { ...arr[idx], info: e.target.value };
                    setForm({ ...form, users: arr });
                  }}
                  placeholder="Info"
                />
                <button
                  onClick={() => {
                    const arr = [...form.users];
                    arr.splice(idx, 1);
                    setForm({ ...form, users: arr });
                  }}
                >
                  Usuń
                </button>
              </div>
            ))}
            <button
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  users: [...(prev.users || []), { uid: '', displayName: '', phone: '', gender: '', info: '' }],
                }))
              }
            >
              + Dodaj uczestnika
            </button>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={createSessionsGroup}>Zapisz grupę i wygeneruj zajęcia</button>
            <button onClick={() => setMode('list')}>Anuluj</button>
          </div>
        </div>
      )}

      {/* MANAGE */}
      {mode === 'manage' && (
        <div style={{ display: 'grid', gap: 16 }}>
          {!selectedSG && (
            <>
              <h3>Zarządzaj istniejącymi</h3>
              {sgList.map((g) => (
                <div
                  key={g.id}
                  style={{ border: '1px solid #ddd', borderRadius: 8, padding: 10, cursor: 'pointer' }}
                  onClick={() => openSG(g)}
                >
                  <div style={{ fontWeight: 700 }}>{locationName(g.location_id)} — {g?.type || g?.groupId}</div>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>
                    {g.startDate} → {g.endDate} • {g?.days?.length || 0} dni • Uczestnicy: {g?.users?.length || 0}
                  </div>
                </div>
              ))}
              {!sgList.length && <div>Brak grup.</div>}
            </>
          )}

          {selectedSG && editSG && (
            <>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <button onClick={() => { setSelectedSG(null); setEditSG(null); }}>← Wróć</button>
                <h3 style={{ margin: 0 }}>Edycja: {locationName(editSG.location_id)} — {editSG?.type || editSG?.groupId}</h3>
              </div>

              {/* META */}
              <div style={{ display: 'grid', gap: 10, border: '1px solid #ddd', borderRadius: 8, padding: 10 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ width: 160 }}>Lokalizacja:</span>
                  <select
                    value={editSG.location_id}
                    onChange={(e) => setEditSG({ ...editSG, location_id: e.target.value })}
                  >
                    {locations.map((l) => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ width: 160 }}>Typ:</span>
                  <input value={editSG.type} onChange={(e) => setEditSG({ ...editSG, type: e.target.value })} />
                </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ width: 160 }}>Kolor:</span>
                  <input value={editSG.color} onChange={(e) => setEditSG({ ...editSG, color: e.target.value })} />
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <div>
                    <div>Min wiek</div>
                    <input
                      type="number"
                      value={editSG.minAge}
                      onChange={(e) => setEditSG({ ...editSG, minAge: e.target.value })}
                    />
                  </div>
                  <div>
                    <div>Max wiek</div>
                    <input
                      type="number"
                      value={editSG.maxAge}
                      onChange={(e) => setEditSG({ ...editSG, maxAge: e.target.value })}
                    />
                  </div>
                  <div>
                    <div>Poziom</div>
                    <input
                      value={editSG.exp}
                      onChange={(e) => setEditSG({ ...editSG, exp: e.target.value })}
                    />
                  </div>
                  <div>
                    <div>Dorośli?</div>
                    <input
                      type="checkbox"
                      checked={!!editSG.adults}
                      onChange={(e) => setEditSG({ ...editSG, adults: e.target.checked })}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <div>
                    <div>Cena umowy</div>
                    <input
                      type="number"
                      value={editSG?.prices?.contract || 0}
                      onChange={(e) => setEditSG({ ...editSG, prices: { ...editSG.prices, contract: e.target.value } })}
                    />
                  </div>
                  <div>
                    <div>Cena wejścia</div>
                    <input
                      type="number"
                      value={editSG?.prices?.entry || 0}
                      onChange={(e) => setEditSG({ ...editSG, prices: { ...editSG.prices, entry: e.target.value } })}
                    />
                  </div>
                  <div>
                    <div>Umowa (obligation)</div>
                    <input
                      value={editSG?.prices?.obligation || ''}
                      onChange={(e) => setEditSG({ ...editSG, prices: { ...editSG.prices, obligation: e.target.value } })}
                    />
                  </div>
                  <div>
                    <div>Miejsca</div>
                    <input
                      type="number"
                      value={editSG.slots || 1}
                      onChange={(e) => setEditSG({ ...editSG, slots: e.target.value })}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 16 }}>
                  <div>
                    <div>Start</div>
                    <input
                      type="date"
                      value={editSG.startDate}
                      onChange={(e) => setEditSG({ ...editSG, startDate: e.target.value })}
                    />
                  </div>
                  <div>
                    <div>Koniec</div>
                    <input
                      type="date"
                      value={editSG.endDate}
                      onChange={(e) => setEditSG({ ...editSG, endDate: e.target.value })}
                    />
                  </div>
                </div>

                {/* dni */}
                <div style={{ borderTop: '1px dashed #ddd', paddingTop: 10 }}>
                  <div style={{ marginBottom: 8, fontWeight: 700 }}>Dni zajęć</div>
                  {(editSG.days || []).map((d, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '110px 120px 120px 220px 1fr', gap: 8, marginBottom: 6 }}>
                      <input
                        type="number"
                        value={d.dayOfWeek}
                        onChange={(e) => {
                          const arr = [...editSG.days];
                          arr[idx] = { ...arr[idx], dayOfWeek: e.target.value };
                          setEditSG({ ...editSG, days: arr });
                        }}
                      />
                      <input
                        type="time"
                        value={d.startTime}
                        onChange={(e) => {
                          const arr = [...editSG.days];
                          arr[idx] = { ...arr[idx], startTime: e.target.value };
                          setEditSG({ ...editSG, days: arr });
                        }}
                      />
                      <input
                        type="time"
                        value={d.endTime}
                        onChange={(e) => {
                          const arr = [...editSG.days];
                          arr[idx] = { ...arr[idx], endTime: e.target.value };
                          setEditSG({ ...editSG, days: arr });
                        }}
                      />
                      <input
                        value={d.instructor}
                        onChange={(e) => {
                          const arr = [...editSG.days];
                          arr[idx] = { ...arr[idx], instructor: e.target.value };
                          setEditSG({ ...editSG, days: arr });
                        }}
                        placeholder="instruktor (email)"
                      />
                      <input
                        value={d.name}
                        onChange={(e) => {
                          const arr = [...editSG.days];
                          arr[idx] = { ...arr[idx], name: e.target.value };
                          setEditSG({ ...editSG, days: arr });
                        }}
                        placeholder="Nazwa"
                      />
                    </div>
                  ))}
                  <button
                    onClick={() =>
                      setEditSG((prev) => ({
                        ...prev,
                        days: [...(prev.days || []), { dayOfWeek: '', startTime: '', endTime: '', instructor: '', name: '' }],
                      }))
                    }
                  >
                    + Dodaj dzień
                  </button>
                </div>

                <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
                  <button onClick={saveSGMeta}>Zapisz zmiany metadanych</button>
                </div>
              </div>

              {/* Uczestnicy + operacje od daty */}
              <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 10 }}>
                <div style={{ marginBottom: 8, fontWeight: 700 }}>Uczestnicy ({editSG?.users?.length || 0})</div>

                {(editSG?.users || []).map((u, idx) => (
                  <div key={`${u.uid || 'x'}-${idx}`} style={{ display: 'grid', gridTemplateColumns: '1fr 140px 100px 1fr 120px 200px', gap: 6, marginBottom: 6 }}>
                    <input
                      value={u.displayName}
                      onChange={(e) => {
                        const arr = [...editSG.users];
                        arr[idx] = { ...arr[idx], displayName: e.target.value };
                        setEditSG({ ...editSG, users: arr });
                      }}
                    />
                    <input
                      value={u.phone}
                      onChange={(e) => {
                        const arr = [...editSG.users];
                        arr[idx] = { ...arr[idx], phone: e.target.value };
                        setEditSG({ ...editSG, users: arr });
                      }}
                      placeholder="Telefon"
                    />
                    <input
                      value={u.gender || ''}
                      onChange={(e) => {
                        const arr = [...editSG.users];
                        arr[idx] = { ...arr[idx], gender: e.target.value };
                        setEditSG({ ...editSG, users: arr });
                      }}
                      placeholder="Płeć"
                    />
                    <input
                      value={u.info || ''}
                      onChange={(e) => {
                        const arr = [...editSG.users];
                        arr[idx] = { ...arr[idx], info: e.target.value };
                        setEditSG({ ...editSG, users: arr });
                      }}
                      placeholder="Info"
                    />

                    {/* Usuń tylko z meta */}
                    <button onClick={() => removeUserFromSG(idx)}>Usuń (meta)</button>

                    {/* Operacja OD DATY */}
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        type="date"
                        defaultValue={toISODate(new Date())}
                        onChange={(e) => {
                          // schowaj tymczasowo na elemencie
                          const arr = [...editSG.users];
                          arr[idx] = { ...arr[idx], __from: e.target.value };
                          setEditSG({ ...editSG, users: arr });
                        }}
                      />
                      <button
                        onClick={() =>
                          applyUsersChangeFromDate({
                            type: 'remove',
                            user: editSG.users[idx],
                            fromDate: editSG.users[idx]?.__from || toISODate(new Date()),
                          })
                        }
                      >
                        Usuń OD daty
                      </button>
                      <button
                        onClick={() =>
                          applyUsersChangeFromDate({
                            type: 'add',
                            user: editSG.users[idx],
                            fromDate: editSG.users[idx]?.__from || toISODate(new Date()),
                          })
                        }
                      >
                        Dodaj OD daty
                      </button>
                    </div>
                  </div>
                ))}

                <button onClick={() => addUserToSG({ uid: '', displayName: '', phone: '', gender: '', info: '' })}>
                  + Dodaj uczestnika (meta)
                </button>

                <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                  <button onClick={saveSGMeta}>Zapisz listę uczestników (meta)</button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
